import { create } from "zustand";
import type { LandscapeData } from "@/types/landscape";
import type { ResultFingerprint } from "@/lib/resultFingerprint";

const ALL_ROLES = new Set([
  "supports", "challenges", "extends", "applies", "operationalises", "reviews",
]);

export type ColorBy = "role" | "cluster";

interface LandscapeStore {
  isOpen: boolean;
  hoveredId: string | null;
  selectedId: string | null;
  visibleRoles: Set<string>;
  /** Which dimension colors the canvas dots. Clusters are navigated by zoom,
   * not filtered — only roles get visibility toggles. */
  colorBy: ColorBy;
  cachedData: LandscapeData | null;
  cachedConceptId: string | null;
  /**
   * ResultFingerprint snapshotted from the Analyze scope at the moment
   * cachedData was fetched. Compared on each render to surface a STALE
   * rail pill when the user dismisses / restores / expands corpus
   * underneath an already-cached map.
   */
  cachedFingerprint: ResultFingerprint | null;
  /**
   * Concept ID whose dot-reveal animation has already played in this
   * session. The Radix dialog unmounts/remounts the canvas on each
   * Landscape open; without this flag the fade-in animation replays
   * every time the user returns from Expand, which reads as the canvas
   * "re-rendering". Set after first paint; reset by clearCache when the
   * underlying data changes (so new corpora animate on first reveal).
   */
  animatedConceptId: string | null;
  open: () => void;
  close: () => void;
  setHoveredId: (id: string | null) => void;
  setSelectedId: (id: string | null) => void;
  toggleRole: (role: string) => void;
  setColorBy: (mode: ColorBy) => void;
  setCachedData: (
    conceptId: string,
    data: LandscapeData,
    fingerprint: ResultFingerprint,
  ) => void;
  markAnimated: (conceptId: string) => void;
  clearCache: () => void;
  reset: () => void;
}

export const useLandscapeStore = create<LandscapeStore>((set) => ({
  isOpen: false,
  hoveredId: null,
  selectedId: null,
  visibleRoles: new Set(ALL_ROLES),
  colorBy: "role",
  cachedData: null,
  cachedConceptId: null,
  cachedFingerprint: null,
  animatedConceptId: null,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false, hoveredId: null, selectedId: null }),
  setHoveredId: (id) => set({ hoveredId: id }),
  setSelectedId: (id) =>
    set((s) => ({ selectedId: s.selectedId === id ? null : id })),
  toggleRole: (role) =>
    set((s) => {
      const next = new Set(s.visibleRoles);
      if (next.has(role)) {
        if (next.size > 1) next.delete(role);
      } else {
        next.add(role);
      }
      return { visibleRoles: next };
    }),
  setColorBy: (mode) => set({ colorBy: mode }),
  setCachedData: (conceptId, data, fingerprint) =>
    set({
      cachedData: data,
      cachedConceptId: conceptId,
      cachedFingerprint: fingerprint,
      // New data → re-animate the reveal once.
      animatedConceptId: null,
    }),
  markAnimated: (conceptId) => set({ animatedConceptId: conceptId }),
  clearCache: () =>
    set({
      cachedData: null,
      cachedConceptId: null,
      cachedFingerprint: null,
      animatedConceptId: null,
    }),
  reset: () =>
    set({
      hoveredId: null,
      selectedId: null,
      visibleRoles: new Set(ALL_ROLES),
      colorBy: "role",
    }),
}));
